"use client";

import type { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import type { UserFestivalStatus } from "@/lib/features/festival/user-festival-status";
import { trackFestivalAttendance } from "@/lib/services/analytics";
import { createClient } from "@/lib/supabase/client";
import type { Database } from "@/lib/types/supabase";
import { trpc } from "@/lib/trpc/react";

/**
 * Real-time festival status hook
 *
 * Modern architecture:
 * - Database is the single source of truth
 * - Real-time events drive all updates
 * - Optimistic updates for instant feedback
 * - No cache invalidation needed (staleTime: Infinity)
 * - Automatic cross-tab/device synchronization
 */

// Custom event for syncing optimistic updates across component instances
const FESTIVAL_STATUS_EVENT = "festival-status-update";

interface FestivalStatusData {
	userId: string;
	festivalId: string;
	status: UserFestivalStatus;
	autoMarked?: boolean;
}

interface UseRealtimeFestivalStatusOptions {
	festivalId: string;
	festivalName?: string;
	userId: string | null;
	enabled?: boolean;
}

export function useRealtimeFestivalStatus({
	festivalId,
	festivalName,
	userId,
	enabled = true,
}: UseRealtimeFestivalStatusOptions) {
	// Local state is our source of truth
	const [status, setStatus] = useState<UserFestivalStatus>(null);
	const [isLoading, setIsLoading] = useState(true);
	const channelRef = useRef<any>(null);
	const instanceId = useRef(Math.random().toString(36).substring(7));
	const supabase = createClient();
	const utils = trpc.useUtils();

	// tRPC mutation for updating status
	const updateStatusMutation = trpc.festival.updateStatus.useMutation({
		onSuccess: async (_data, { festivalId: mutationFestivalId, status: newStatus }) => {
			// CRITICAL: Invalidate user festival data for immediate status updates on detail page
			if (userId) {
				await utils.festival.getUserFestivalData.invalidate({
					userId,
					festivalId: mutationFestivalId,
				});
			}

			// Invalidate facepile data for immediate updates
			await utils.festival.getFacepileData.invalidate();

			// Invalidate festival discovery cache — lazy to avoid refetching all loaded infinite query pages
			await utils.festival.list.invalidate(undefined, { refetchType: 'none' });

			// Invalidate profile caches with refetchType: 'all' to force refetch
			// on mounted components (SPA tabs stay mounted)
			await utils.user.getProfileFestivals.invalidate(
				undefined,
				{ refetchType: 'all' }
			);

			const message =
				newStatus === null
					? "Removed festival status"
					: `Festival marked as "${getStatusLabel(newStatus)}"`;
			toast.success(message);
		},
		onError: (err) => {
			// On error, real-time will correct the state
			const errorMessage = err.message?.includes("UNAUTHORIZED")
				? "Please sign in to update festival status"
				: err.message?.includes("Rate limit")
					? err.message
					: "Failed to update festival status";

			toast.error(errorMessage);
		},
	});

	// Initial data fetch - happens ONCE, never goes stale
	useEffect(() => {
		if (!userId || !festivalId || !enabled) {
			setIsLoading(false);
			setStatus(null);
			return;
		}

		async function fetchInitialStatus() {
			if (!userId) return;
			
			try {
				type AttendanceSelect = Pick<Database["public"]["Tables"]["festival_attendance"]["Row"], "status">;
				
				const { data, error } = await supabase
					.from("festival_attendance")
					.select("status")
					.eq("user_id", userId)
					.eq("festival_id", festivalId)
					.maybeSingle() as { data: AttendanceSelect | null; error: any };

				if (!error && data) {
					setStatus(data.status as UserFestivalStatus);
				} else {
					setStatus(null);
				}
			} catch (error) {
				console.error("Error fetching initial festival status:", error);
				setStatus(null);
			} finally {
				setIsLoading(false);
			}
		}

		fetchInitialStatus();
	}, [userId, festivalId, enabled, supabase]);

	// Real-time subscription - this is where all updates come from
	useEffect(() => {
		if (!userId || !festivalId || !enabled) return;

		// Unique channel per instance to avoid conflicts
		const channelName = `festival-status:${festivalId}:${userId}:${instanceId.current}`;

		const channel = supabase
			.channel(channelName)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "festival_attendance",
					filter: `user_id=eq.${userId}`,
				},
				(payload: RealtimePostgresChangesPayload<FestivalStatusData>) => {
					// Client-side filter for festival_id (Supabase Realtime only supports 1 filter)
					const record = payload.eventType === "DELETE" ? payload.old : payload.new;
					if ((record as any)?.festival_id !== festivalId) {
						return;
					}

					// Real-time update from database
					if (payload.eventType === "DELETE") {
						setStatus(null);
					} else if (payload.new) {
						setStatus(payload.new.status);
					}
				},
			)
			.subscribe((status) => {
				// Handle subscription errors gracefully
				if (status === "CHANNEL_ERROR") {
					console.warn(
						`[Realtime] Channel error for festival status. Will retry on reconnect.`
					);
				} else if (status === "TIMED_OUT") {
					console.warn(
						`[Realtime] Connection timeout for festival status. Will retry.`
					);
				}
			});

		channelRef.current = channel;

		return () => {
			if (channelRef.current) {
				supabase.removeChannel(channelRef.current);
			}
		};
	}, [userId, festivalId, enabled, supabase]);

	// Listen for optimistic updates from other button instances on the same page
	useEffect(() => {
		if (!userId || !festivalId) return;

		const handleOptimisticUpdate = (event: CustomEvent) => {
			const {
				userId: eventUserId,
				festivalId: eventFestivalId,
				status: eventStatus,
			} = event.detail;
			// Check if this event is for our festival
			if (eventUserId === userId && eventFestivalId === festivalId) {
				setStatus(eventStatus);
			}
		};

		window.addEventListener(
			FESTIVAL_STATUS_EVENT as any,
			handleOptimisticUpdate as any,
		);

		return () => {
			window.removeEventListener(
				FESTIVAL_STATUS_EVENT as any,
				handleOptimisticUpdate as any,
			);
		};
	}, [userId, festivalId]);

	// Update status with optimistic updates
	const updateStatus = async (newStatus: UserFestivalStatus) => {
		if (!userId) {
			toast.error("Please sign in to update festival status");
			return;
		}

		// 1. Optimistic update - instant UI feedback
		setStatus(newStatus);

		// 2. Broadcast to other instances on the page
		const event = new CustomEvent(FESTIVAL_STATUS_EVENT, {
			detail: {
				userId,
				festivalId,
				status: newStatus,
			},
		});
		window.dispatchEvent(event);

		// 3. Track analytics for attendance changes
		if (newStatus) {
			// Map internal status to analytics status
			const analyticsStatus = newStatus === "going" || newStatus === "went"
				? "going"
				: newStatus === "want_to_go"
					? "interested"
					: "not_going";
			trackFestivalAttendance(festivalId, festivalName || "Festival", analyticsStatus);
		}

		// 4. Send to database
		try {
			await updateStatusMutation.mutateAsync({
				festivalId,
				status: newStatus,
			});
		} catch (error) {
			// Error handled in mutation onError
			// Real-time will correct the state if needed
		}

		// 5. Real-time will confirm/correct in ~100ms
	};

	return {
		status,
		updateStatus,
		isLoading,
		isPending: updateStatusMutation.isPending,
	};
}

// Helper function for status labels
function getStatusLabel(status: UserFestivalStatus): string {
	switch (status) {
		case "want_to_go":
			return "Want to Go";
		case "going":
			return "Going";
		case "went":
			return "Went";
		case "watched_online":
			return "Saw Stream";
		default:
			return "Want to Go";
	}
}
